import type { ReactNode } from 'react';
import type { ExpectedField, ImportTarget } from '@discovr/types';
import { Info } from '../../../icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';
import { MAX_PARSED_AMOUNT } from './amountParse';
import {
  MAX_OUTREACH_ROWS,
  MAX_SHEET_ROWS_LABEL,
  MAX_SHEET_SIZE_LABEL,
  MAX_USAGE_RIGHTS_DAYS,
  MIN_USAGE_RIGHTS_DAYS,
  formatLimit,
} from './importLimits';
import { MAX_NAME_LENGTH, USAGE_RIGHTS_DAYS_KEY } from './importWizardHelpers';
import { contentMetricsCopy } from './contentMetricsCopy';

interface SheetRulesPopoverProps {
  /** Default 'pipeline'. An outreach sheet has its own, much shorter rules. */
  target?: ImportTarget;
  /** Campaign payout model — drives the ₹0 line (allowed only on gifting / revenue-share). */
  payoutModel?: string;
  /** Hides the YouTube-only username line on Instagram campaigns and words
   *  where fetched metrics come from (reels or uploads). Unknown (wizard still
   *  loading) shows every rule. */
  campaignPlatform?: string;
  /** The server's own column list. Read here only for `required` on the metric
   *  columns: optional where the metrics job fetches engagement rate and views
   *  (Instagram, YouTube), required where it does not run. Deriving it from the
   *  server's answer keeps this text honest without duplicating the rule on
   *  the client. */
  expectedFields?: ExpectedField[];
}

const INSTAGRAM_PLATFORM = 'instagram';

const Code = ({ children }: { children: ReactNode }) => (
  <code className="rounded-sm bg-muted px-1 font-mono text-caption-regular text-foreground">{children}</code>
);

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 text-[9px] font-bold tracking-widest text-muted-foreground uppercase">
        {title}
      </div>
      <ul className="list-disc space-y-1 pl-4 marker:text-muted-foreground">{children}</ul>
    </div>
  );
}

function RulesPopover({ children }: { children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          <Info size={12} /> Sheet rules
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        // max-h caps the height when rendered inside a modal so the popover
        // doesn't grow past the visible area. onWheel stopPropagation prevents
        // react-remove-scroll (used by Radix Dialog) from intercepting wheel
        // events at the document level — without it, two-finger trackpad scroll
        // is blocked because the popover Portal sits outside the Dialog subtree.
        onWheel={(e) => e.stopPropagation()}
        className="max-h-[min(520px,var(--radix-popover-content-available-height))] w-[540px] overflow-y-auto p-4 text-caption-medium/relaxed text-muted-foreground"
      >
        <div className="mb-3 text-body-sm-semibold text-foreground">Sheet import rules</div>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Outreach takes a handle and an optional email; the outreach jobs fetch the rest. */
function OutreachRules() {
  return (
    <>
      <Section title="File">
        <li>
          Paste handles, or upload an <Code>.xlsx or .csv</Code> file — up to {formatLimit(MAX_OUTREACH_ROWS)} creators
          and {MAX_SHEET_SIZE_LABEL}.
        </li>
      </Section>
      <Section title="Columns">
        <li>
          <Code>username</Code> (or <Code>handle</Code>) — the creator's Instagram handle or profile link. Required.
        </li>
        <li>
          <Code>email</Code> — optional. Becomes the creator's contact email when they have none yet. An address
          that isn't valid is left out and the creator is still imported.
        </li>
        <li>Every other column is ignored: names, followers and rates are fetched after the import.</li>
      </Section>
      <Section title="When a row is skipped">
        <li>The creator is already in this campaign, in outreach or in the pipeline.</li>
        <li>The handle is missing, not a valid Instagram handle, or a link to another platform.</li>
        <li>The same handle appears more than once — review asks you to keep one.</li>
      </Section>
    </>
  );
}

interface PipelineRulesProps {
  payoutModel?: string;
  campaignPlatform?: string;
  expectedFields?: ExpectedField[];
}

/**
 * A grouped, payout-aware reference of the sheet import / re-import rules, shown
 * next to the upload control so the admin team knows the conventions without
 * tribal knowledge. The ₹0 line flips on the campaign's payout model.
 */
function PipelineRules({ payoutModel, campaignPlatform, expectedFields }: PipelineRulesProps) {
  const zeroAllowed = payoutModel === 'gifting_only' || payoutModel === 'revenue_share';
  const metricsRequired = expectedFields?.some((f) => f.key === 'engagement_rate' && f.required) ?? true;
  // Before the server answers, show every rule rather than hide one that applies.
  const offersField = (key: string) => expectedFields?.some((f) => f.key === key) ?? true;
  const showYoutubeLinkRule = campaignPlatform !== INSTAGRAM_PLATFORM;
  return (
    <>
      <Section title="File">
        <li>
          Upload an <Code>.xlsx or .csv</Code> file — up to {MAX_SHEET_ROWS_LABEL} creators and {MAX_SHEET_SIZE_LABEL}.
        </li>
      </Section>

      <Section title="Required columns">
        <li>
          <Code>username</Code> (or <Code>un</Code>) — the creator's handle (see Usernames
          below).
        </li>
        {metricsRequired && (
          <li>
            <Code>engagement_rate</Code> (or <Code>eng_rate</Code> / <Code>er</Code>) and{' '}
            <Code>avg_views</Code> (or <Code>av</Code>) — must be numbers greater than zero.
          </li>
        )}
      </Section>

      <Section title="Optional columns">
        {!metricsRequired && (
          <li>
            <Code>engagement_rate</Code> and <Code>avg_views</Code> — leave them out and we read
            them off the creator's {contentMetricsCopy(campaignPlatform).recentContent} after the
            import. Include them and your numbers are used as-is.
          </li>
        )}
        <li>
          <Code>first_name</Code> (or <Code>fn</Code>) and <Code>last_name</Code> (or{' '}
          <Code>ln</Code>) — used to pre-fill the creator's display name.
        </li>
        {offersField('followers') && (
          <li>
            <Code>followers</Code> — Followers (Subscribers on YouTube), a whole number, 0 or more.
          </li>
        )}
        {offersField(USAGE_RIGHTS_DAYS_KEY) && (
          <li>
            <Code>{USAGE_RIGHTS_DAYS_KEY}</Code> — a whole number of days, {MIN_USAGE_RIGHTS_DAYS}–{MAX_USAGE_RIGHTS_DAYS}. It needs a usage
            rights price too; days without a price are flagged at the review step.
          </li>
        )}
        <li>
          <Code>email</Code> — ignored if present (no longer needed).
        </li>
        <li>
          Deliverable columns accept a short platform prefix — <Code>ig_</Code> instead of{' '}
          <Code>instagram_</Code>, <Code>yt_</Code> instead of <Code>youtube_</Code> (e.g.{' '}
          <Code>instagram_non_collab_reel</Code> or <Code>ig_non_collab_reel</Code>).
        </li>
        <li>Column headers are flexible — capitalisation, spaces, and underscores all work.</li>
      </Section>

      <Section title="When a row is skipped">
        <li>The username is missing or blank.</li>
        {metricsRequired && (
          <li>Engagement rate or average views is missing or not a positive number.</li>
        )}
        <li>A name column is longer than {MAX_NAME_LENGTH} characters.</li>
        <li>
          The same username appears more than once <em>with identical values</em> — the first row
          is kept, the rest dropped. If the duplicate rows disagree, review pauses and asks you to
          pick the one to keep.
        </li>
        <li>
          No <strong>main</strong> deliverable ends up priced. Add-ons alone are not enough — a row
          that prices only an add-on is still skipped.
        </li>
      </Section>

      <Section title="Deliverable prices">
        <li>
          <strong>Any positive number</strong> — sets that deliverable at the given price.
        </li>
        <li className={zeroAllowed ? 'font-medium text-success' : 'font-medium text-destructive'}>
          <strong>₹0</strong> —{' '}
          {zeroAllowed
            ? 'allowed on this campaign type (gifting / revenue-share). Imports the deliverable as free/barter.'
            : 'not a valid price on this campaign type. Treated as blank — that deliverable is skipped.'}
        </li>
        <li>
          Currency symbols and commas are fine — <Code>₹5,000</Code> imports as 5000.
        </li>
        <li>
          <strong>Units are read automatically</strong> — <Code>12k</Code> imports as 12000,{' '}
          <Code>1.5 lakh</Code> as 150000, <Code>2 crore</Code> as 20000000, and{' '}
          <Code>5000/-</Code> as 5000. Only text that still isn't a readable amount after that is
          flagged at the review step for you to retype.
        </li>
        <li>
          <strong>Extreme values are rejected</strong> — anything over ₹{formatLimit(MAX_PARSED_AMOUNT)} (in either
          direction) is treated as unreadable rather than imported as-is.
        </li>
        <li>
          <strong>Blank or wordy</strong> (e.g. "free", "50%") — that deliverable is skipped. The
          rest of the row is still imported.
        </li>
        <li>
          <strong>The word "remove"</strong> — removes that deliverable from the creator entirely.
        </li>
        <li>
          <strong>A negative number</strong> (e.g. <Code>-1</Code>) is flagged at the review step,
          not read as a removal — retype it as a positive price or the word "remove".
        </li>
      </Section>

      <Section title="Re-importing">
        <li>
          Upload the sheet again at any time — new creators are added, existing ones are updated.
          No creator is ever removed by a re-import.
        </li>
        <li>
          Prices are only updated while the creator is still <Code>pending</Code>. Once a brand
          accepts them, their agreed prices are locked — re-importing refreshes their stats but
          won't change prices.
        </li>
        <li>
          A stat column you leave out is left alone, not blanked — existing figures stay until the
          next platform fetch replaces them.
        </li>
      </Section>

      <Section title="Usernames">
        <li>
          <Code>@handle</Code>, full profile links, different capitalisation — they all resolve to
          the same creator.
        </li>
        <li>
          You can paste a profile link straight into the Username column, with or without{' '}
          <Code>https://</Code> (e.g. <Code>instagram.com/name</Code>). We turn it into the handle.
        </li>
        {showYoutubeLinkRule && (
          <li>
            YouTube <Code>/channel/</Code> links can't be resolved to a handle — paste the
            channel's <Code>@handle</Code> link instead (<Code>youtube.com/@name</Code>).
          </li>
        )}
        <li>
          A profile link from the wrong platform (a YouTube link in an Instagram campaign) is
          flagged at the review step rather than imported.
        </li>
      </Section>
    </>
  );
}

export function SheetRulesPopover({ target = 'pipeline', ...rules }: SheetRulesPopoverProps) {
  return <RulesPopover>{target === 'outreach' ? <OutreachRules /> : <PipelineRules {...rules} />}</RulesPopover>;
}
